import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { CompanyService } from 'src/app/utils/services/company/company.service';

@Component({
  selector: 'app-company-switch-modal',
  templateUrl: './company-switch-modal.component.html',
  styleUrls: ['./company-switch-modal.component.scss']
})
export class CompanySwitchModalComponent implements OnInit {
  companies: any[] = [];
  selectedCompany: any = null;
  modalVisible = false;
  confirmationVisible = false; // For the confirmation dialog
  
  @Output() onCompanySelected: EventEmitter<any> = new EventEmitter<any>();

  constructor(private companyService: CompanyService) { }

  ngOnInit(): void {
    console.log('CompanySwitchModalComponent initialized');
    // Load companies from JSON
    this.loadCompanies();
  }

  loadCompanies() {
    console.log('Loading companies...');
    this.companyService.getCompanies().subscribe(
      (companies) => {
        console.log('Companies loaded successfully:', companies);
        this.companies = companies;
        // After loading companies, check if we need to select one based on localStorage
        setTimeout(() => {
          this.loadSelectedCompany();
          console.log('Companies in component:', this.companies);
        }, 100);
      },
      (error) => {
        console.error('Error loading companies:', error);
        // Fallback to sample data
        this.companies = [
          {
            id: 1,
            name: "Company A",
            code: "COMP_A"
          },
          {
            id: 2,
            name: "Company B",
            code: "COMP_B"
          },
          {
            id: 3,
            name: "Company C",
            code: "COMP_C"
          }
        ];
        console.log('Using fallback companies:', this.companies);
        // After setting fallback companies, check if we need to select one based on localStorage
        setTimeout(() => {
          this.loadSelectedCompany();
        }, 100);
      }
    );
  }

  loadSelectedCompany() {
    // Load selected company from localStorage
    const savedCompany = localStorage.getItem('selectedCompany');
    if (savedCompany) {
      try {
        const companyData = JSON.parse(savedCompany);
        // Find the company in our companies array
        const company = this.companies.find(c => c.id === companyData.id);
        if (company) {
          this.selectedCompany = company;
          console.log('Loaded selected company from localStorage:', this.selectedCompany);
        } else {
          // If the saved company is not in the list, clear the localStorage
          console.log('Saved company not found in companies list, clearing localStorage');
          localStorage.removeItem('selectedCompany');
        }
      } catch (e) {
        console.error('Error parsing selected company from localStorage:', e);
        // Clear invalid data from localStorage
        localStorage.removeItem('selectedCompany');
      }
    }
  }

  isSelected(company: any): boolean {
    // Check if this company is the selected one in the component
    if (this.selectedCompany) {
      return this.selectedCompany.id === company.id;
    }
    
    return false;
  }

  selectCompany(company: any) {
    console.log('Company selected:', company);
    this.selectedCompany = company;
    // Note: We no longer save to localStorage here
    // localStorage is only updated when the selection is confirmed
    console.log('Selected company updated in component (not saved yet):', this.selectedCompany);
  }

  // Show confirmation dialog instead of directly confirming
  showConfirmation() {
    console.log('Showing confirmation dialog for company:', this.selectedCompany);
    if (this.selectedCompany) {
      this.confirmationVisible = true;
    } else {
      console.log('No company selected');
    }
  }

  // Cancel the confirmation dialog
  cancelConfirmation() {
    console.log('Cancelled company switch confirmation');
    this.confirmationVisible = false;
    // Reset to current company when cancelling confirmation
    this.resetToCurrentCompany();
  }

  // Proceed with the selection after confirmation
  proceedWithSelection() {
    console.log('Proceeding with company selection:', this.selectedCompany);
    this.confirmationVisible = false;
    this.modalVisible = false;
    // Save to localStorage only when selection is confirmed
    if (this.selectedCompany) {
      localStorage.setItem('selectedCompany', JSON.stringify(this.selectedCompany));
      console.log('Selected company confirmed and saved to localStorage:', this.selectedCompany);
      
      // Also store a flag to indicate that the user has manually selected a company
      localStorage.setItem('userSelectedCompany', 'true');
      
      // Clear browser history and navigate to home to prevent back navigation to pages from different company
      this.clearBrowserHistory();
    }
    console.log('Emitting company selected event');
    this.onCompanySelected.emit(this.selectedCompany);
  }

  confirmSelection() {
    // This method is now deprecated as we're using showConfirmation instead
    console.log('Confirming selection, selected company:', this.selectedCompany);
    if (this.selectedCompany) {
      this.modalVisible = false;
      // Save to localStorage only when selection is confirmed
      localStorage.setItem('selectedCompany', JSON.stringify(this.selectedCompany));
      console.log('Selected company confirmed and saved to localStorage:', this.selectedCompany);
      
      // Also store a flag to indicate that the user has manually selected a company
      localStorage.setItem('userSelectedCompany', 'true');
      
      // Clear browser history and navigate to home to prevent back navigation to pages from different company
      this.clearBrowserHistory();
      
      console.log('Emitting company selected event');
      this.onCompanySelected.emit(this.selectedCompany);
    } else {
      console.log('No company selected');
    }
  }

  closeModal() {
    console.log('Closing company switch modal');
    this.modalVisible = false;
    // Reset to current company when closing modal
    this.resetToCurrentCompany();
  }
  
  open() {
    console.log('Opening company switch modal');
    // Reset the selection to the current company when opening the modal
    this.resetToCurrentCompany();
    this.modalVisible = true;
    // Add a small delay to ensure the change detection
    setTimeout(() => {
      console.log('Modal visibility:', this.modalVisible);
    }, 100);
  }
  
  /**
   * Reset the selected company to the current company stored in localStorage
   * This ensures that any temporary selection is cleared when reopening the modal
   */
  resetToCurrentCompany(): void {
    console.log('Resetting to current company');
    // Load the current company from localStorage
    const savedCompany = localStorage.getItem('selectedCompany');
    if (savedCompany) {
      try {
        const companyData = JSON.parse(savedCompany);
        // Find the company in our companies array
        const company = this.companies.find(c => c.id === companyData.id);
        if (company) {
          this.selectedCompany = company;
          console.log('Reset selected company to current:', this.selectedCompany);
        } else {
          console.log('Current company not found in companies list');
          // If current company is not in the list, select the first available company
          if (this.companies.length > 0) {
            this.selectedCompany = this.companies[0];
            console.log('Defaulting to first company:', this.selectedCompany);
          }
        }
      } catch (e) {
        console.error('Error parsing selected company from localStorage:', e);
      }
    } else {
      console.log('No current company found in localStorage');
      // If no company is stored, select the first available company
      if (this.companies.length > 0) {
        this.selectedCompany = this.companies[0];
        console.log('Defaulting to first company:', this.selectedCompany);
      }
    }
  }

  /**
   * Clear browser history and navigate to home page
   * This prevents users from navigating back to pages from a different company
   */
  private clearBrowserHistory(): void {
    console.log('Clearing browser history and navigating to home');
    // Replace the current history entry with home page
    // This prevents users from using the back button to return to pages from the previous company
    // Also add a history entry to prevent back navigation
    window.history.pushState(null, '', '/home');
    window.addEventListener('popstate', function() {
      window.history.pushState(null, '', '/home');
    });
  }
}